import type { RangeOpt } from '../modules/DateRangeSelect';

// ─────────────────────────────────────────────────────────────────────────────
// Reagent Preparation — shared types for the three list screens.
// Ported from legacy ALPACA `ReagentPreparationLogViewController`;
// spec `ReagentPreparation_Spec.md` §7 (log list), §16.1 (LLT Search),
// §16.2 (LLT Depletion).  Mirrors the API DTOs in
// `Application/ReagentPrep/ReagentPrepLogModels.cs`.
// ─────────────────────────────────────────────────────────────────────────────

/** The chip codes the API accepts (spec §7). Anything else means "All". */
export type ReagentDateFilter = '1M' | '3M' | '6M' | '1Y' | '2Y' | '5Y' | 'All';

/**
 * The Date Filter options for these screens (a dropdown, like every other form).  SEVEN options — the shared
 * `TRACKING_RANGES` has only five (no 2Y / 5Y), and reagent preparations are
 * kept for years, so the longer windows are the ones analysts actually reach
 * for.  `days` is only the shared component's identity key; the API is driven
 * by `label`, so the values need not be exact calendar arithmetic — the real
 * cutoff is computed server-side by `dbo.AI_DateFilterCutoff`, in ONE place.
 */
export const REAGENT_RANGES: RangeOpt[] = [
  { label: '1M', days: 30 },
  { label: '3M', days: 90 },
  { label: '6M', days: 180 },
  { label: '1Y', days: 365 },
  { label: '2Y', days: 730 },
  { label: '5Y', days: 1825 },
  { label: 'All', days: null },
];

/** Chip code → the shared component's `days` key. */
export function rangeDays(code: ReagentDateFilter): number | null {
  return REAGENT_RANGES.find((r) => r.label === code)?.days ?? null;
}

/** The shared component's `days` key → chip code. Unknown → 'All'. */
export function rangeCode(days: number | null): ReagentDateFilter {
  return (REAGENT_RANGES.find((r) => r.days === days)?.label ?? 'All') as ReagentDateFilter;
}

/**
 * Which category a preparation belongs to — legacy `PrepSelectTypes`, stored as
 * its declaration ordinal.  Decides which header form a row opens (spec §3/§7)
 * and which report layout renders it.
 */
export const PREP_SELECT_TYPE: Record<number, string> = {
  0: 'Chemical Reagent Prep',
  1: 'Calibration Set Prep',
  2: 'Micro Media and Reagent Prep',
};

/** Reagent Prep Log list row (spec §7). */
export interface ReagentPrepListRow {
  oid:             string;
  llt:             string | null;
  division:        string | null;
  prepType:        string | null;
  prepName:        string | null;
  department:      string | null;
  expirationDate:  string | null;
  preparedBy:      string | null;
  datePrepared:    string | null;
  /** Not displayed — picks the header form on open. */
  prepSelectType:  number | null;
}

/** LLT Search row (spec §16.1) — as above plus Department. */
export interface LltSearchRow extends ReagentPrepListRow {
  department: string | null;
}

/** LLT Depletion row (spec §16.2). */
export interface LltDepletionRow {
  oid:            string;
  llt:            string | null;
  /** Grid caption is "StandardName" (legacy ColumnInfo Caption). */
  prepName:       string | null;
  /** Legacy computed `FinalVolumeDepletion` — the first component's FinalVol_V2.
   *  A string because every legacy measurement column is nvarchar (§20 quirk 1);
   *  displayed verbatim, never parsed. */
  finalVolume:    string | null;
  prepType:       string | null;
  department:     string | null;
  dateDepleted:   string | null;
  depletedBy:     string | null;
  expirationDate: string | null;
  datePrepared:   string | null;
  preparedBy:     string | null;
}

/** What a deplete actually changed — empty when every id was already depleted. */
export interface DepletedRow {
  oid:          string;
  llt:          string | null;
  dateDepleted: string | null;
  depletedBy:   string | null;
}

// ── Detail views (the three header forms) ───────────────────────────────────

/** Header solvent mode (legacy `Solvent` enum, declaration order). */
export const SOLVENT_MODES = [
  { value: 0, label: 'N/A' },
  { value: 1, label: 'Sample As Solvent' },
  { value: 2, label: 'Water As Solvent' },
] as const;

/** Calibration stock source (legacy `StockSolution` enum, spec §12.1). */
export const STOCK_SOLUTIONS = [
  { value: 0, label: 'Vendor Stock' },
  { value: 1, label: 'Lab Stock' },
] as const;

/** One reagent-preparation header, as the detail views bind it. Mirrors
 *  `ReagentPrepDetail` on the API. FKs arrive as BOTH an id and display text so
 *  the read-only view needs no second lookup round-trip. */
export interface ReagentPrepDetail {
  oid: string;
  llt: string | null;
  prepSelectType: number | null;
  prepTypeId: string | null;      prepTypeName: string | null;
  prepNameId: string | null;      prepNameText: string | null;
  // Test is a MULTI-select in XAF (';'-joined Oids + strTest display text).
  testIds: string[];              testText: string | null;
  departmentId: string | null;    departmentName: string | null;
  divisionId: string | null;      divisionName: string | null;
  expirationDate: string | null;
  preparedById: string | null;    preparedByName: string | null;
  datePrepared: string | null;
  setupDate: string | null;
  solvent: number | null;
  boolSolvent: boolean | null;
  solventID: string | null;
  storageId: string | null;       storageName: string | null;
  comment: string | null;
  dateDepleted: string | null;
  depletedById: string | null;
  // Calibration-only (spec §12.1)
  selectStockSolution: number | null;
  stockConc: number | null;
  stockConcUnitId: string | null;         stockConcUnitName: string | null;
  vendorStockId: string | null;           vendorStockName: string | null;
  ltId: string | null;                    ltText: string | null;
  labStockId: string | null;              labStockName: string | null;
  lltLabStockId: string | null;           lltLabStockText: string | null;
  calibrationSolventId: string | null;    calibrationSolventName: string | null;
  calibrationSolventLtId: string | null;  calibrationSolventLtText: string | null;
  noOfLevels: number | null;
}

/** One component (Chemistry / Micro) or level (Calibration). Every measurement
 *  is a STRING — the legacy columns are all nvarchar (§20 quirk 1) and this
 *  wave only displays them, so they are shown verbatim rather than parsed and
 *  reformatted, which would silently rewrite what the analyst typed. */
export interface ReagentPrepComponent {
  oid: string;
  componentID: number | null;
  // FK ids as well as display text: the Prep Notepad edits by id (its lookups
  // store an Oid) while the grids show the name.
  vendorStockId: string | null;
  ltId: string | null;
  labStockId: string | null;
  lltId: string | null;
  calculationApproachId: string | null;
  w1UnitsId: string | null; v1UnitsId: string | null; c1UnitsId: string | null;
  v2UnitsId: string | null; c2UnitsId: string | null;
  vendorStockName: string | null;
  ltText: string | null;          ltExpDate: string | null;
  labStockName: string | null;
  lltText: string | null;         lltExpDate: string | null;
  solvent: boolean | null;
  calculationApproachName: string | null;
  v1UnitsName: string | null;  w1UnitsName: string | null;
  c1UnitsName: string | null;  v2UnitsName: string | null;  c2UnitsName: string | null;
  stockConc_C1: string | null;   volumeTaken_V1: string | null;
  weight_g_w1: string | null;    finalVol_V2: string | null;  finalConc_C2: string | null;
  purity: string | null;  density: string | null;
  mw: string | null;      eqWt: string | null;  constant: string | null;
  formula: string | null; comment: string | null;
  // Micro Media (§11.2)
  phCriteria: string | null;  ph: string | null;
  autoclave: string | null;   filterSterilization: string | null;
  sporeGrowth: string | null; positiveControl: string | null; negativeControl: string | null;
  // Calibration levels (§12.3)
  workingStdName: string | null;  stockStdName: string | null;
  // The unit Ids travel too: the levels grid is batch-editable (XAF
  // InlineEditMode="Batch") and its unit combos write an Id.
  calWsUnitsId?: string | null; calV1UnitsId?: string | null;
  calV2UnitsId?: string | null; calC2UnitsId?: string | null;
  cal_Weight_g_w1: string | null;    calWsUnitsName: string | null;
  cal_VolumeTaken_V1: string | null; calV1UnitsName: string | null;
  cal_FinalVol_V2: string | null;    calV2UnitsName: string | null;
  cal_FinalConc_C2: string | null;   calC2UnitsName: string | null;
  subLLT: string | null;
}

/** What a detail form POSTs. The server owns LLT, BoolSolvent, PreparedBy,
 *  Division and SetupDate, so none of them are sent. Department is sent: it is
 *  pre-filled but editable (null = the server's default rule). */
export interface ReagentPrepInput {
  prepSelectType: number;
  prepTypeId: string | null;
  prepNameId: string | null;
  testIds: string[] | null;
  expirationDate: string | null;
  datePrepared: string | null;
  solvent: number | null;
  solventID: string | null;
  storageId: string | null;
  comment: string | null;
  departmentId: string | null;
  selectStockSolution: number | null;
  stockConc: number | null;
  stockConcUnitId: string | null;
  vendorStockId: string | null;
  ltId: string | null;
  labStockId: string | null;
  lltLabStockId: string | null;
  calibrationSolventId: string | null;
  calibrationSolventLtId: string | null;
  noOfLevels: number | null;
}

export interface ReagentLookupOption { id: string; name: string | null }

/**
 * The "N/A" first option of every Reagent Prep dropdown — a CLIENT-ONLY
 * placeholder (owner request 2026-10-16). It means "no value": it is what an
 * empty field shows and highlights, and picking it CLEARS the field. Its value
 * is '' / null, so "N/A" is never sent to or stored by the API.
 */
export const NA_OPTION = { value: '', label: 'N/A' } as const;
